import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Building2, Phone, Mail, Calendar, DollarSign, User, MapPin, Zap, Plus, Search, Filter, Edit, Trash2, X, Save, ChevronRight } from 'lucide-react';

// ── Logo / Avatar ──────────────────────────────────────────────
function CompanyLogo({ name, domain, size = 44 }) {
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const gradients = [['#3B82F6','#22D3A5'],['#8B5CF6','#3B82F6'],['#F472B6','#8B5CF6'],['#FBBF24','#F472B6'],['#34D399','#22D3A5'],['#60A5FA','#A78BFA'],['#FB923C','#FBBF24'],['#F87171','#FB923C'],['#22D3EE','#3B82F6'],['#A78BFA','#F472B6']];
  const hash = name.split('').reduce((a,c)=>a+c.charCodeAt(0),0);
  const [c1,c2] = gradients[hash % gradients.length];
  const initials = name.split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase();
  const logoUrl = domain ? `https://logo.clearbit.com/${domain}?size=${size*2}` : null;
  const fallback = (
    <div style={{ width:size, height:size, minWidth:size, borderRadius:'10px', background:`linear-gradient(135deg,${c1},${c2})`, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--nd-text-strong)', fontWeight:'800', fontSize:size*0.33, letterSpacing:'0.5px' }}>
      {initials}
    </div>
  );
  if (!logoUrl || err) return fallback;
  return (
    <div style={{ position:'relative', width:size, height:size, minWidth:size }}>
      {loading && <div style={{ position:'absolute', inset:0, borderRadius:'10px', background:`linear-gradient(135deg,${c1},${c2})`, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--nd-text-strong)', fontWeight:'800', fontSize:size*0.33 }}>{initials}</div>}
      <img src={logoUrl} alt={name} style={{ width:size, height:size, borderRadius:'10px', objectFit:'cover', display:loading?'none':'block', border:'1px solid var(--nd-w08)' }} onLoad={()=>setLoading(false)} onError={()=>{setErr(true);setLoading(false);}}/>
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────
const inputS = { width:'100%', padding:'10px 12px', background:'var(--nd-w04)', border:'1px solid var(--nd-w08)', borderRadius:'9px', color:'var(--nd-text)', fontSize:'13px', fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', colorScheme:'dark', transition:'border-color .2s' };
const labelS = { display:'block', fontSize:'10px', fontWeight:'700', color:'var(--nd-tx30)', letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:'7px' };
const th = { padding:'11px 16px', fontSize:'10px', fontWeight:'700', color:'var(--nd-tx25)', textTransform:'uppercase', letterSpacing:'0.12em', textAlign:'left', borderBottom:'1px solid var(--nd-w05)', background:'var(--nd-w02)', whiteSpace:'nowrap' };
const td = { padding:'13px 16px', fontSize:'13px', color:'#94A3B8', borderBottom:'1px solid var(--nd-w04)' };

const CommercialClientList = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ client_name:"", contact_person:"", contact_number:"", email:"", address:"", contract_type:"commercial", contract_start_date:"", total_system_size:"", sales_price_before_tax:"", sales_agent:"", billing_type:"monthly", contractual_services_count:"" });

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients`);
      if (r.data.success) setClients(r.data.data.clients);
      else setError("Failed to load clients");
    } catch { setError("Failed to load clients. Please try again."); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await axios.patch(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients/${id}/deactivate`); fetchClients(); }
    catch { alert("Failed to delete client"); }
  };

  const handleEditClick = (client) => {
    setEditingClient(client);
    setFormData({ client_name:client.client_name||"", contact_person:client.contact_person||"", contact_number:client.contact_number||"", email:client.email||"", address:client.address||"", contract_type:client.contract_type||"commercial", contract_start_date:client.contract_start_date?client.contract_start_date.split('T')[0]:"", total_system_size:client.total_system_size||"", sales_price_before_tax:client.sales_price_before_tax||"", sales_agent:client.sales_agent||"", billing_type:client.billing_type||"monthly", contractual_services_count:client.contractual_services_count||"" });
    setShowEditModal(true);
  };

  const handleCloseModal = () => { setShowEditModal(false); setEditingClient(null); };
  const handleChange = (e) => { const {name,value}=e.target; setFormData(p=>({...p,[name]:value})); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name||!formData.contact_person||!formData.contact_number) { alert("Fill all required fields"); return; }
    setSaving(true);
    try {
      const r = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients/${editingClient.id}`, formData);
      if (r.data.success) { handleCloseModal(); fetchClients(); }
    } catch { alert("Failed to update client."); }
    finally { setSaving(false); }
  };

  const filteredClients = clients.filter(c => {
    const ok = [c.client_name,c.contact_person,c.sales_agent].some(v=>v?.toLowerCase().includes(searchTerm.toLowerCase()));
    return ok && (filterType==="all" || c.contract_type===filterType);
  });

  const contractPill = (type) => {
    const map = { commercial:{ bg:'rgba(59,130,246,0.10)', border:'rgba(59,130,246,0.20)', color:'#60A5FA' }, industrial:{ bg:'rgba(251,191,36,0.10)', border:'rgba(251,191,36,0.20)', color:'#FBBF24' } };
    const s = map[type] || map.commercial;
    return <span style={{ background:s.bg, border:`1px solid ${s.border}`, color:s.color, padding:'3px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em' }}>{type}</span>;
  };

  const billingPill = (type) => (
    <span style={{ background: type==='monthly' ? 'rgba(52,211,153,0.09)' : 'rgba(167,139,250,0.09)', border:`1px solid ${type==='monthly' ? 'rgba(52,211,153,0.18)' : 'rgba(167,139,250,0.18)'}`, color: type==='monthly' ? '#34D399' : '#A78BFA', padding:'3px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:'700' }}>
      {type==='monthly' ? 'Monthly' : 'Per Service'}
    </span>
  );

  const F = ({ label, name, type='text', required, span, options }) => (
    <div style={span ? { gridColumn:'1 / -1' } : {}}>
      <label style={labelS}>{label}{required && <span style={{ color:'#F87171' }}> *</span>}</label>
      {options ? (
        <select name={name} value={formData[name]} onChange={handleChange} required={required} style={{ ...inputS, cursor:'pointer', appearance:'none' }}
          onFocus={e=>{e.target.style.borderColor='rgba(34,211,165,0.38)';}} onBlur={e=>{e.target.style.borderColor='var(--nd-w08)';}}>
          {options.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : span==='textarea' ? (
        <textarea name={name} value={formData[name]} onChange={handleChange} rows={2} style={{ ...inputS, resize:'vertical' }}
          onFocus={e=>e.target.style.borderColor='rgba(34,211,165,0.38)'} onBlur={e=>e.target.style.borderColor='var(--nd-w08)'}/>
      ) : (
        <input type={type} name={name} value={formData[name]} onChange={handleChange} required={required} style={inputS}
          onFocus={e=>e.target.style.borderColor='rgba(34,211,165,0.38)'} onBlur={e=>e.target.style.borderColor='var(--nd-w08)'}/>
      )}
    </div>
  );

  return (
    <div style={{ padding:'14px 20px', fontFamily:"'DM Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap'); @keyframes ccl-spin{to{transform:rotate(360deg)}} .ccl-row:hover td{background:var(--nd-w02)!important} select option{background:var(--nd-surface-2);color:var(--nd-text)}`}</style>

      {/* Header */}
      <div style={{ background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'14px', padding:'20px 24px 18px', marginBottom:'18px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', fontWeight:'700', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--nd-tx25)', marginBottom:'10px' }}>
          <span>Commercial</span><ChevronRight size={12} color="var(--nd-w12)"/><span style={{ color:'rgba(34,211,165,0.55)' }}>C&I Clients</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:'12px', marginBottom:'14px' }}>
          <div>
            <div style={{ fontSize:'26px', fontWeight:'800', color:'var(--nd-tx85)', letterSpacing:'-0.03em', display:'flex', alignItems:'center', gap:'12px' }}><Building2 size={22} color="#3B82F6"/>C&I Clients Management</div>
            <div style={{ fontSize:'12px', color:'var(--nd-tx30)', marginTop:'5px' }}>Commercial & Industrial Solar Projects</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'28px', fontWeight:'700', lineHeight:1, background:'linear-gradient(90deg,#3B82F6,#22D3A5)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>{clients.length}</div>
              <div style={{ fontSize:'11px', color:'var(--nd-tx25)', fontWeight:'600', letterSpacing:'0.08em', textTransform:'uppercase', marginTop:'2px' }}>Clients</div>
            </div>
            <button onClick={() => navigate('/commercial/add-clients')} style={{ padding:'10px 18px', background:'linear-gradient(90deg,#3B82F6,#22D3A5)', color:'var(--nd-text-strong)', border:'none', borderRadius:'10px', fontWeight:'700', fontSize:'13px', cursor:'pointer', display:'flex', alignItems:'center', gap:'7px', fontFamily:"'DM Sans',sans-serif", boxShadow:'0 4px 14px rgba(34,211,165,0.18)' }}>
              <Plus size={15}/>Add Client
            </button>
          </div>
        </div>
        <div style={{ height:'2px', borderRadius:'2px', background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }}/>
      </div>

      {/* Toolbar */}
      <div style={{ background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'12px', padding:'14px 18px', marginBottom:'14px', display:'flex', gap:'12px', alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:'240px' }}>
          <Search size={14} color="var(--nd-tx22)" style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
          <input type="text" placeholder="Search by name, contact, or sales agent…" value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
            style={{ ...inputS, paddingLeft:'36px' }}
            onFocus={e=>e.target.style.borderColor='rgba(34,211,165,0.38)'} onBlur={e=>e.target.style.borderColor='var(--nd-w08)'}/>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <Filter size={14} color="var(--nd-tx25)"/>
          <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ ...inputS, width:'auto', padding:'10px 14px', cursor:'pointer', appearance:'none' }}>
            <option value="all">All Types</option>
            <option value="commercial">Commercial</option>
            <option value="industrial">Industrial</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'14px', overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:'60px 20px', textAlign:'center' }}>
            <div style={{ width:'36px', height:'36px', border:'3px solid var(--nd-w08)', borderTop:'3px solid #3B82F6', borderRadius:'50%', animation:'ccl-spin 1s linear infinite', margin:'0 auto' }}/>
            <p style={{ color:'var(--nd-tx25)', fontSize:'13px', marginTop:'14px' }}>Loading clients…</p>
          </div>
        ) : error ? (
          <div style={{ padding:'48px 20px', textAlign:'center' }}>
            <div style={{ fontSize:'13px', color:'#F87171', marginBottom:'14px' }}>⚠ {error}</div>
            <button onClick={fetchClients} style={{ padding:'9px 20px', background:'linear-gradient(90deg,#3B82F6,#22D3A5)', color:'var(--nd-text-strong)', border:'none', borderRadius:'9px', fontWeight:'700', fontSize:'13px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Retry</button>
          </div>
        ) : filteredClients.length === 0 ? (
          <div style={{ padding:'60px 20px', textAlign:'center' }}>
            <Building2 size={40} color="var(--nd-w10)" style={{ marginBottom:'14px' }}/>
            <p style={{ fontSize:'14px', fontWeight:'700', color:'var(--nd-tx40)', margin:'0 0 6px' }}>No clients found</p>
            <p style={{ fontSize:'13px', color:'var(--nd-tx22)' }}>{searchTerm ? 'Try adjusting your search' : 'Add your first client to get started'}</p>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={th}>Client</th>
                <th style={th}>Contact</th>
                <th style={th}>Contract</th>
                <th style={th}>Value / Size</th>
                <th style={th}>Billing</th>
                <th style={{ ...th, textAlign:'right' }}>Actions</th>
              </tr></thead>
              <tbody>
                {filteredClients.map(client => (
                  <tr key={client.id} className="ccl-row">
                    <td style={td}>
                      <div style={{ display:'flex', alignItems:'center', gap:'11px' }}>
                        <CompanyLogo name={client.client_name} domain={client.email?.split('@')[1]} size={42}/>
                        <div>
                          <div style={{ fontWeight:'700', color:'var(--nd-text)', fontSize:'14px', marginBottom:'3px' }}>{client.client_name}</div>
                          <div style={{ fontSize:'11px', color:'var(--nd-tx30)', display:'flex', alignItems:'center', gap:'4px' }}><User size={11}/>{client.sales_agent}</div>
                        </div>
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight:'600', color:'var(--nd-text)', fontSize:'13px', marginBottom:'3px' }}>{client.contact_person}</div>
                      <div style={{ fontSize:'12px', color:'var(--nd-tx30)', display:'flex', alignItems:'center', gap:'4px' }}><Phone size={11}/>{client.contact_number}</div>
                      {client.email && <div style={{ fontSize:'12px', color:'var(--nd-tx30)', display:'flex', alignItems:'center', gap:'4px', marginTop:'2px' }}><Mail size={11}/>{client.email}</div>}
                    </td>
                    <td style={td}>
                      {contractPill(client.contract_type)}
                      <div style={{ fontSize:'11px', color:'var(--nd-tx25)', marginTop:'5px', display:'flex', alignItems:'center', gap:'4px' }}><Calendar size={11}/>{new Date(client.contract_start_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>
                    </td>
                    <td style={td}>
                      <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:'700', color:'#34D399', fontSize:'13px', marginBottom:'3px' }}>PKR {parseFloat(client.sales_price_before_tax).toLocaleString('en-IN')}</div>
                      <div style={{ fontSize:'11px', color:'var(--nd-tx30)', display:'flex', alignItems:'center', gap:'3px' }}><Zap size={11}/>{parseFloat(client.total_system_size).toLocaleString()} kW</div>
                    </td>
                    <td style={td}>
                      {billingPill(client.billing_type)}
                      {client.billing_type==='per_service_count' && client.contractual_services_count && (
                        <div style={{ fontSize:'11px', color:'var(--nd-tx25)', marginTop:'4px' }}>{client.contractual_services_count} services</div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign:'right' }}>
                      <div style={{ display:'flex', gap:'7px', justifyContent:'flex-end' }}>
                        <button onClick={()=>handleEditClick(client)} style={{ padding:'7px', borderRadius:'7px', border:'none', background:'rgba(251,191,36,0.10)', color:'#FBBF24', cursor:'pointer', display:'flex' }}><Edit size={15}/></button>
                        <button onClick={()=>handleDelete(client.id,client.client_name)} style={{ padding:'7px', borderRadius:'7px', border:'none', background:'rgba(248,113,113,0.10)', color:'#F87171', cursor:'pointer', display:'flex' }}><Trash2 size={15}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.70)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000, padding:'20px' }} onClick={e=>e.target===e.currentTarget&&handleCloseModal()}>
          <div style={{ background:'var(--nd-surface)', border:'1px solid var(--nd-w08)', borderRadius:'16px', maxWidth:'860px', width:'100%', maxHeight:'90vh', overflow:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.60)' }}>
            {/* Modal header */}
            <div style={{ padding:'18px 24px', borderBottom:'1px solid var(--nd-w06)', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'var(--nd-surface)', zIndex:2 }}>
              <h2 style={{ fontSize:'15px', fontWeight:'800', color:'var(--nd-text)', display:'flex', alignItems:'center', gap:'9px', margin:0 }}><Edit size={16} color="#3B82F6"/>Edit Client — <span style={{ background:'linear-gradient(90deg,#3B82F6,#22D3A5)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>{editingClient?.client_name}</span></h2>
              <button onClick={handleCloseModal} style={{ width:'30px', height:'30px', borderRadius:'7px', background:'var(--nd-w05)', border:'1px solid var(--nd-w08)', color:'var(--nd-tx40)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><X size={15}/></button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding:'22px 24px' }}>
              {/* Client Info */}
              <div style={{ marginBottom:'22px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:'var(--nd-tx35)', letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px' }}><Building2 size={13} color="#3B82F6"/>Client Information</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'14px' }}>
                  <F label="Client Name" name="client_name" required/>
                  <F label="Contract Type" name="contract_type" required options={[{v:'commercial',l:'Commercial'},{v:'industrial',l:'Industrial'}]}/>
                  <F label="Sales Agent" name="sales_agent" required/>
                  <F label="Contract Start Date" name="contract_start_date" type="date" required/>
                </div>
              </div>

              {/* Contact */}
              <div style={{ marginBottom:'22px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:'var(--nd-tx35)', letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px' }}><User size={13} color="#3B82F6"/>Contact Information</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'14px' }}>
                  <F label="Contact Person" name="contact_person" required/>
                  <F label="Contact Number" name="contact_number" type="tel" required/>
                  <F label="Email" name="email" type="email"/>
                  <F label="Address" name="address" span="textarea"/>
                </div>
              </div>

              {/* System & Pricing */}
              <div style={{ marginBottom:'22px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:'var(--nd-tx35)', letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px' }}><Zap size={13} color="#3B82F6"/>System & Pricing</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'14px' }}>
                  <F label="System Size (kW)" name="total_system_size" type="number" required/>
                  <F label="Sales Price (Before Tax)" name="sales_price_before_tax" type="number" required/>
                </div>
              </div>

              {/* Billing */}
              <div style={{ marginBottom:'22px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:'var(--nd-tx35)', letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px' }}><DollarSign size={13} color="#3B82F6"/>Billing Configuration</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'14px' }}>
                  <F label="Billing Type" name="billing_type" required options={[{v:'monthly',l:'Monthly'},{v:'per_service_count',l:'Per Service Count'}]}/>
                  {formData.billing_type==='per_service_count' && <F label="Contractual Services Count" name="contractual_services_count" type="number" required/>}
                </div>
              </div>

              {/* Footer */}
              <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end', paddingTop:'16px', borderTop:'1px solid var(--nd-w06)' }}>
                <button type="button" onClick={handleCloseModal} style={{ padding:'10px 20px', background:'var(--nd-w05)', border:'1px solid var(--nd-w08)', borderRadius:'9px', color:'var(--nd-tx45)', fontWeight:'700', fontSize:'13px', cursor:'pointer', display:'flex', alignItems:'center', gap:'7px', fontFamily:"'DM Sans',sans-serif" }}>
                  <X size={14}/>Cancel
                </button>
                <button type="submit" disabled={saving} style={{ padding:'10px 22px', background:saving?'var(--nd-w10)':'linear-gradient(90deg,#3B82F6,#22D3A5)', color:'var(--nd-text-strong)', border:'none', borderRadius:'9px', fontWeight:'700', fontSize:'13px', cursor:saving?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:'7px', opacity:saving?0.6:1, fontFamily:"'DM Sans',sans-serif", boxShadow:'0 4px 14px rgba(34,211,165,0.18)' }}>
                  {saving ? <><span style={{ width:'13px', height:'13px', border:'2px solid var(--nd-tx25)', borderTop:'2px solid white', borderRadius:'50%', animation:'ccl-spin .7s linear infinite', display:'inline-block' }}/>Saving…</> : <><Save size={14}/>Update Client</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommercialClientList;